import DeleteAlertDialog from "@/components/DeleteAlertDialog";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import { ResponseStatus } from "@/domain/entities/agenda/AgendaItemResponseStatus";
import EventDetail from "@/domain/entities/emails/EventDetail";
import useManageInvitationToEvent from "@/hooks/agenda/useManageInvitationToEvent";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { cn } from "@/lib/utils";
import { Check, X } from "lucide-react";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

const TooltipWrapper = ({
    content,
    compact,
    children,
}: {
    content: string;
    compact: boolean;
    children: ReactNode;
}) =>
    compact ? (
        <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent>{content}</TooltipContent>
        </Tooltip>
    ) : (
        children
    );

type EventInvitationProps = {
    setUpdatedEvent: (event: AgendaItem | EventDetail) => void;
    updatedEvent: AgendaItem | EventDetail;
    className?: string;
    isSingleEvent?: boolean;
    compact?: boolean;
};

const EventInvitation = ({
    setUpdatedEvent,
    updatedEvent,
    className,
    isSingleEvent = false,
    compact = false,
}: EventInvitationProps) => {
    const { t } = useTranslation();
    const { data: user } = useGetUserProfile();

    const { acceptInvitation, rejectInvitation, maybeInvitation } =
        useManageInvitationToEvent(setUpdatedEvent, updatedEvent);

    const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

    if (!user) {
        return null;
    }

    const userAcceptedStatus = updatedEvent.getUserAcceptedStatus(user);

    const activeButtonClassName =
        "border border-primary-dark bg-primary-dark text-white";

    return (
        <TooltipProvider delayDuration={0}>
            <div
                className={cn(
                    "event-invitation flex gap-2 px-2 py-2",
                    isSingleEvent && !compact
                        ? "mt-1 [&_button]:flex-1 [&_button]:py-3 [&_button]:text-base"
                        : "flex-wrap justify-end",
                    className,
                )}
            >
                <TooltipWrapper
                    compact={compact}
                    content={t(
                        "dashboard.widgets.calendar.event.invitation.accept",
                    )}
                >
                    <Button
                        className={cn(
                            userAcceptedStatus === ResponseStatus.Accepted &&
                                activeButtonClassName,
                            isSingleEvent && "rounded-full p-3",
                        )}
                        onClick={() => acceptInvitation.mutate()}
                        size={isSingleEvent ? "sm" : "xs"}
                        variant="outline"
                    >
                        <Check className="h-3 w-3" />
                        {!compact &&
                            t(
                                "dashboard.widgets.calendar.event.invitation.accept",
                            )}
                    </Button>
                </TooltipWrapper>
                <TooltipWrapper
                    compact={compact}
                    content={t(
                        "dashboard.widgets.calendar.event.invitation.reject",
                    )}
                >
                    <Button
                        className={cn(
                            userAcceptedStatus === ResponseStatus.Declined &&
                                activeButtonClassName,
                            isSingleEvent && "rounded-full p-3",
                        )}
                        onClick={() => setIsRejectDialogOpen(true)}
                        size={isSingleEvent ? "sm" : "xs"}
                        variant="outline"
                    >
                        <X className="h-3 w-3" />
                        {!compact &&
                            t(
                                "dashboard.widgets.calendar.event.invitation.reject",
                            )}
                    </Button>
                </TooltipWrapper>
                <DeleteAlertDialog
                    description={t(
                        "dashboard.widgets.calendar.event.invitation.rejectDescription",
                    )}
                    handleDeleteAction={() => rejectInvitation.mutate()}
                    title={`${t(
                        "dashboard.widgets.calendar.event.invitation.reject",
                    )} ${updatedEvent.subject}`}
                    open={isRejectDialogOpen}
                    onOpenChange={setIsRejectDialogOpen}
                />
                <TooltipWrapper
                    compact={compact}
                    content={t(
                        "dashboard.widgets.calendar.event.invitation.maybe",
                    )}
                >
                    <Button
                        className={cn(
                            userAcceptedStatus ===
                                ResponseStatus.TentativelyAccepted &&
                                activeButtonClassName,
                            userAcceptedStatus ===
                                ResponseStatus.TentativelyAccepted &&
                                "border border-dashed border-white",
                            isSingleEvent && "rounded-full p-3",
                        )}
                        onClick={() => maybeInvitation.mutate()}
                        size={isSingleEvent ? "sm" : "xs"}
                        variant="outline"
                    >
                        <span>?</span>
                        {!compact &&
                            t(
                                "dashboard.widgets.calendar.event.invitation.maybe",
                            )}
                    </Button>
                </TooltipWrapper>
            </div>
        </TooltipProvider>
    );
};

export default EventInvitation;
